import React, { useState, useEffect, useRef } from 'react';
import dotProp from 'dot-prop-immutable';
import { InView } from 'react-intersection-observer';
import { AnimatePresence, motion } from 'framer-motion';

/* Icons */
import FilterIcon from '~/../symbols/filter.svg';

/* Components */
import Filter from '../filters/Filter';
import FilterCollapse from '../filters/FilterCollapse';

/* Common */
import { Button, Icon } from '~/components/common';

/* Utilities */
import { trans } from '~/utilities';

/* Constants */
import { fadeInOut } from '~/constants/animations'

export default function ProductOverviewFilter({
    filters,
    updateFilters,
    isDesktop,
    isSticky,
    horizontal,
    handleResetFilter,
    filtersShowAll,
    setFiltersShowAll
}) {
    const [openGroup, setOpenGroup] = useState(null);
    const filterRefs = useRef([]);
    const scrollRef = useRef(null);
    const [inViewStart, setInViewStart] = useState(false);
    const [inViewEnd, setInViewEnd] = useState(false);

    const toggleGroup = (groupPath) => {
        setOpenGroup(prev => prev === groupPath ? null : groupPath);
    };

    const handleClickOutside = (event) => {
        const clickedOutsideFilters = filterRefs.current.every(
            (ref) => ref && !ref.contains(event.target)
        );

        if (clickedOutsideFilters) {
            setOpenGroup((prev) => {
                if (prev !== null) {
                    return null;
                } else {
                    return prev;
                }
            });
        }
    };

    const calculateVisibleFilters = () => {
        if (horizontal || !isDesktop) return;
        const container = document.querySelector('.filters__inner');
        const items = document.querySelectorAll('.filter-list__item');
        let containerWidth = container.clientWidth - 50; // Subtract 160 pixels (more / less item)
        let totalWidth = 0;

        items.forEach((item, index) => {
            item.classList.remove('hidden');
            totalWidth += item.offsetWidth + 10; // Add 10 pixels (gap)

            if (totalWidth > containerWidth) {
                item.classList.add('hidden');
            }
        });
    };

    const updateChoicesFilter = (selectedFilters) => {
        const index = filters.findIndex((filter) => filter.key === selectedFilters[0].filterKey);
        if (index === -1) return;
        const newFilters = dotProp.set(filters, `${ index }.filters`, selectedFilters);

        updateFilters(newFilters);
    };

    const updateRangeFilter = (filter, filterIndex, value, type) => {
        const newFilters = filters;
        const index = filters.findIndex((f) => f.key === filter.filterKey);
        if (index === -1) return;

        if (type === 'min') {
            newFilters[index].filters[filterIndex].selectedMinValue = parseInt(value);
        } else if (type === 'max') {
            newFilters[index].filters[filterIndex].selectedMaxValue = parseInt(value);
        } else if (type === 'choices') {
            newFilters[index].filters[filterIndex].choices = value;
        } else if (type === undefined) {
            newFilters[index].filters[filterIndex].value = value;
        }

        updateFilters(newFilters);
    };

    const getActiveCount = (filterGroup) => {
        const countActiveChoices = (choices) => {
            return choices.reduce((count, choice) => {
                if (choice.active) {
                    count++;
                }
                if (choice.filters && choice.filters.length > 0) {
                    count += countActiveChoices(choice.filters[0].choices);
                }
                return count;
            }, 0);
        };

        if (filterGroup?.filters[0]?.viewType === 'multi_select') {
            return countActiveChoices(filterGroup?.filters[0]?.choices);
        } else if (filterGroup?.filters[0]?.viewType === 'range_list') {
            const { selectedMinValue, selectedMaxValue } = filterGroup?.filters[0];
            return (selectedMinValue > 0 || selectedMaxValue < filterGroup?.filters[0].maxValues[filterGroup?.filters[0].maxValues.length - 1]) ? 1 : 0;
        }
        return 0;
    };

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside); // mobile
        window.addEventListener('resize', calculateVisibleFilters);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside); // mobile
            window.removeEventListener('resize', calculateVisibleFilters);
        };
    }, []);

    useEffect(() => {
        // If only one brand filter set as active
        filters.map((filter) => {
            if (filter.key === 'brand') {
                if (filter.filters && filter.filters[0].choices.length === 1) {
                    filter.filters[0].choices[0].active = true;
                }
            }
        });

        calculateVisibleFilters();
    }, [filters]);

    return (
        <div className={`filters ${!filtersShowAll ? 'filters--hidden' : ''} ${ isDesktop ? 'filters--desktop' : '' }  ${ horizontal ? 'filters--horizontal bust-out' : '' } ${ isSticky ? 'filters--fixed' : '' } ${inViewStart ? 'scroll-start' : ''} ${inViewEnd ? 'scroll-end' : ''}`}>
            <AnimatePresence>
                {openGroup && (
                    <motion.div key="overlay" className="overlay d-lg-none" {...fadeInOut} onClick={() => { setOpenGroup(null); }}></motion.div>
                )}
            </AnimatePresence>
            <div className="filters__inner">
                <ul ref={scrollRef} className={`filter-list ${ filtersShowAll ? 'filter-list__show-all' : '' }`}>
                <InView as="div" className="filter-list-start" root={scrollRef.current} onChange={(inView) => setInViewStart(inView)} />
                <li className="filter-list__item d-none d-lg-block">
                        <div className="align-items-center gap-2 font-weight-bold text--16 mr-4 d-flex py-2">
                            <Icon
                                size={24}
                                url={FilterIcon.url}
                            />
                            {trans('app.vehicle_overview.filter_title')}
                        </div>
                    </li>
                    {filters.map((filterGroup, index) => {
                        const filterGroupPath = `${ index }`;
                        const isOpen = openGroup === filterGroupPath;
                        const activeCount = getActiveCount(filterGroup);

                        return (
                            <li
                                key={filterGroupPath}
                                className="filter-list__item"
                                ref={(el) => filterRefs.current[index] = el}
                            >
                                <FilterCollapse
                                    filterKey={filterGroup.key}
                                    eventKey={filterGroupPath}
                                    title={filterGroup.name?.replace(filterGroup.key, '')?.trim()}
                                    isOpen={isOpen}
                                    resetFilter={handleResetFilter}
                                    onToggle={() => toggleGroup(filterGroupPath)}
                                    activeCount={activeCount}
                                    gradient={filterGroup.filters[0].viewType === 'multi_select'}
                                    colorView={filterGroup.filters[0].filterKey === 'basiskleur' ? true : false}
                                >
                                    <Filter
                                        filterGroup={filterGroup}
                                        filterGroupIndex={index}
                                        filters={filterGroup.filters}
                                        updateRangeFilter={updateRangeFilter}
                                        updateChoicesFilter={updateChoicesFilter}
                                    />
                                </FilterCollapse>
                            </li>
                        );
                    })}
                    {filtersShowAll ? (
                        <li className="filter-list__item">
                            <Button
                                title={trans('app.vehicle_overview.filter_less')}
                                theme="outline-base"
                                className={`btn btn--collapse--more d-none d-lg-block font-weight-bold text-base text--16`}
                                onClick={() => setFiltersShowAll(false)}
                            />
                        </li>
                    ) : null}
                    <InView as="div" className="filter-list-end" root={scrollRef.current} onChange={(inView) => setInViewEnd(inView)} />
                </ul>
                {!filtersShowAll ? (
                    <div className="filters__more filters__more--absolute bg-white d-none d-lg-block">
                        <Button
                            title={trans('app.vehicle_overview.filter_more')}
                            theme="outline-base"
                            className={`btn btn--collapse--more d-none d-lg-block font-weight-bold text--16`}
                            onClick={() => setFiltersShowAll(true)}
                        />
                    </div>
                ) : null}
            </div>
        </div>
    );
}
